import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import DateTimeField from "@/components/ui/date-time-field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import ReferencePointSelect from "@/components/ui/reference-point-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SubmitButton } from "@/components/ui/submit-button";
import { Textarea } from "@/components/ui/textarea";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { errorHandler } from "@/lib/utils";
import { visitService } from "@/services/api/visits";
import { visitObjectiveService } from "@/services/api/visitObjectives";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

const formSchema = z
  .object({
    reference_point_id: z.number().min(1, "Selecione um ponto de referência"),
    visit_objective_id: z.number().nullable().optional(),
    start_datetime: z.string().min(1, "Data/hora de entrada é obrigatória"),
    end_datetime: z.string().min(1, "Data/hora de saída é obrigatória"),
    notes: z
      .string()
      .max(500, "Observações devem ter no máximo 500 caracteres")
      .nullable(),
  })
  .refine(
    (data) => {
      if (!data.start_datetime || !data.end_datetime) return true;
      const start = new Date(data.start_datetime);
      const end = new Date(data.end_datetime);
      return end > start;
    },
    {
      message: "Data/hora de saída deve ser posterior à data/hora de entrada",
      path: ["end_datetime"],
    },
  )
  .refine(
    (data) => {
      if (!data.start_datetime) return true;
      const start = new Date(data.start_datetime);
      const now = new Date();
      return start <= now;
    },
    {
      message: "Data/hora de entrada não pode ser no futuro",
      path: ["start_datetime"],
    },
  )
  .refine(
    (data) => {
      if (!data.end_datetime) return true;
      const end = new Date(data.end_datetime);
      const now = new Date();
      return end <= now;
    },
    {
      message: "Data/hora de saída não pode ser no futuro",
      path: ["end_datetime"],
    },
  );

type FormValues = z.infer<typeof formSchema>;

const VisitForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [objectives, setObjectives] = useState<{ id: number; title: string }[]>(
    [],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reference_point_id: 0,
      visit_objective_id: null,
      start_datetime: "",
      end_datetime: "",
      notes: null,
    },
  });

  useEffect(() => {
    const fetchObjectives = async () => {
      try {
        const response = await visitObjectiveService.index({
          page: 1,
          per_page: 100,
        });
        setObjectives(response.data);
      } catch (err) {
        errorHandler(err, "Erro ao buscar objetivos de visita");
      }
    };

    fetchObjectives();
  }, []);

  useEffect(() => {
    const loadVisit = async () => {
      if (id) {
        try {
          const response = await visitService.show(Number(id));
          const visitData = response.data;

          // Converter datetime de yyyy-MM-dd HH:mm:ss para yyyy-MM-dd'T'HH:mm:ss
          const startDatetime = visitData.start_datetime
            ? visitData.start_datetime.replace(" ", "T")
            : "";
          const endDatetime = visitData.end_datetime
            ? visitData.end_datetime.replace(" ", "T")
            : "";

          form.reset({
            reference_point_id: visitData.reference_point_id,
            visit_objective_id: visitData.visit_objective_id,
            start_datetime: startDatetime,
            end_datetime: endDatetime,
            notes: visitData.notes,
          });
        } catch (err) {
          errorHandler(err, "Erro ao carregar visita");
          navigate("/visits");
        }
      }
    };

    loadVisit();
  }, [id, form.reset, navigate, form]);

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: visitService.create,
    updateService: visitService.update,
    onSuccess: () => navigate("/visits"),
  });

  const onSubmit = async (values: FormValues) => {
    await handleSubmit(values);
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>{id ? "Editar Visita" : "Nova Visita"}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="reference_point_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Ponto de Referência</FormLabel>
                  <ReferencePointSelect
                    value={field.value}
                    onChange={field.onChange}
                    showType="non-headquarters"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="visit_objective_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Objetivo da Visita</FormLabel>
                  <Select
                    onValueChange={(val) =>
                      field.onChange(val === "none" ? null : Number(val))
                    }
                    value={field.value?.toString() || "none"}
                  >
                    <FormControl>
                      <SelectTrigger className="dark:border-white">
                        <SelectValue placeholder="Selecione um objetivo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {objectives.map((obj) => (
                        <SelectItem key={obj.id} value={obj.id.toString()}>
                          {obj.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DateTimeField
              form={form}
              name="start_datetime"
              label="Data/Hora de Entrada"
            />

            <DateTimeField
              form={form}
              name="end_datetime"
              label="Data/Hora de Saída"
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ""}
                      rows={4}
                      className="dark:border-white"
                      placeholder="Observações sobre a visita (opcional, máximo 500 caracteres)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/visits")}
              >
                Cancelar
              </Button>
              <SubmitButton loading={loading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default VisitForm;
